// Détection des traits trop fins pour l'impression DTF : ouverture morphologique
// (érosion + dilatation de rayon 0,25 mm) à l'échelle d'impression choisie.
// L'encre qui disparaît à l'ouverture = traits/points de moins de 0,5 mm d'épais.

import { autoSpeckleArea, despeckle, dustAreaPx } from './despeckle';

const MIN_STROKE_MM = 0.5;
const TARGET_PX_PER_MM = 12;
const MAX_PIXELS = 4_000_000;
// part de l'encre sous 0,5 mm à partir de laquelle on alerte (les pointes de
// coins rognées par l'ouverture restent bien en dessous)
const RATIO_ALERT = 0.02;
const MIN_THIN_PX = 60;
// critère absolu : surface (mm²) de traits fins à plus de 2r des zones épaisses.
// Les rognures de coins restent à ~0,4r du survivant ; seuls de vrais traits
// (isolés ou dépassant ≥1 mm) vont aussi loin → détecte les traits minoritaires
// (<2 % de l'encre) d'un logo par ailleurs massif.
// 20.10.2026 : 0,5 → 1 mm² — à 0,5 mm², deux cous de goutte de 0,4 mm² (ou quelques pixels de bord selon le navigateur)
// suffisaient à marquer « à vérifier » un logo que l'on venait d'épaissir
const FAR_MM2_ALERT = 1.0;
const MIN_FAR_PX = 20;
// 20.10.2026 : la dilatation (retour) est d'un pas diagonal plus large que l'érosion. Le chamfer 3-4 n'est pas
// isotrope (±8 % selon l'angle) : sans cette marge, un liseré d'UN pixel restait « fin » le long des bords courbes
// — assez long pour passer le filtre des vrais traits à 8 px/mm, et un logo épaissi restait en alerte à 20 cm.
export const DILATE_SLACK = 4;

/**
 * Ne garde dans `isThin` que les composantes (8-voisinage) qui ressemblent à un VRAI trait : au moins ~0,25 mm² et
 * ~1,2 mm d'étendue. Les autres (pointe ou coin de liseré, grain de poussière collé au dessin) sont retirées — on ne les
 * signale pas et on ne les épaissit pas (Daniel 09.10 : pastilles rondes aux coins après « Épaissir »).
 * Retourne le nombre de pixels fins restants. Modifie `isThin` en place.
 */
export function keepRealThinStrokes(isThin: Uint8Array, W: number, H: number, pxPerMm: number): number {
	const minArea = Math.max(16, Math.round(0.25 * pxPerMm * pxPerMm));
	const minExtent = Math.max(6, Math.round(1.2 * pxPerMm));
	const n = W * H;
	const seen = new Uint8Array(n);
	const stack = new Int32Array(n);
	const comp: number[] = [];
	let kept = 0;
	for (let start = 0; start < n; start++) {
		if (!isThin[start] || seen[start]) continue;
		let sp = 0;
		stack[sp++] = start;
		seen[start] = 1;
		comp.length = 0;
		let minX = W;
		let maxX = -1;
		let minY = H;
		let maxY = -1;
		while (sp) {
			const p = stack[--sp];
			comp.push(p);
			const x = p % W;
			const y = (p - x) / W;
			if (x < minX) minX = x;
			if (x > maxX) maxX = x;
			if (y < minY) minY = y;
			if (y > maxY) maxY = y;
			for (let dy = -1; dy <= 1; dy++) {
				const yy = y + dy;
				if (yy < 0 || yy >= H) continue;
				for (let dx = -1; dx <= 1; dx++) {
					const xx = x + dx;
					if ((dx | dy) === 0 || xx < 0 || xx >= W) continue;
					const q = yy * W + xx;
					if (isThin[q] && !seen[q]) {
						seen[q] = 1;
						stack[sp++] = q;
					}
				}
			}
		}
		if (comp.length < minArea || Math.max(maxX - minX, maxY - minY) + 1 < minExtent) {
			for (const p of comp) isThin[p] = 0;
		} else kept += comp.length;
	}
	return kept;
}

const imgCache = new Map<string, Promise<HTMLImageElement>>();
const resultCache = new Map<string, boolean>();

const loadImage = (src: string): Promise<HTMLImageElement> => {
	let p = imgCache.get(src);
	if (!p) {
		p = new Promise((resolve, reject) => {
			const im = new Image();
			im.onload = () => resolve(im);
			im.onerror = () => reject(new Error('image load failed'));
			im.src = src;
		});
		imgCache.set(src, p);
		if (imgCache.size > 100) imgCache.delete(imgCache.keys().next().value as string);
	}
	return p;
};

// Chamfer 3-4 : distance (×3 par pixel orthogonal) de chaque pixel "hors masque" au masque.
// dist doit être initialisé à 0 sur le masque source et à INF ailleurs.
const chamfer = (dist: Int32Array, w: number, h: number): void => {
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			const i = y * w + x;
			let d = dist[i];
			if (x > 0 && dist[i - 1] + 3 < d) d = dist[i - 1] + 3;
			if (y > 0) {
				if (dist[i - w] + 3 < d) d = dist[i - w] + 3;
				if (x > 0 && dist[i - w - 1] + 4 < d) d = dist[i - w - 1] + 4;
				if (x < w - 1 && dist[i - w + 1] + 4 < d) d = dist[i - w + 1] + 4;
			}
			dist[i] = d;
		}
	}
	for (let y = h - 1; y >= 0; y--) {
		for (let x = w - 1; x >= 0; x--) {
			const i = y * w + x;
			let d = dist[i];
			if (x < w - 1 && dist[i + 1] + 3 < d) d = dist[i + 1] + 3;
			if (y < h - 1) {
				if (dist[i + w] + 3 < d) d = dist[i + w] + 3;
				if (x < w - 1 && dist[i + w + 1] + 4 < d) d = dist[i + w + 1] + 4;
				if (x > 0 && dist[i + w - 1] + 4 < d) d = dist[i + w - 1] + 4;
			}
			dist[i] = d;
		}
	}
};

/**
 * 20.10.2026 (Daniel : « Prêt » chez moi, « À vérifier » sur son Chrome avec le MÊME fichier) : le rééchantillonnage de
 * drawImage dépend du navigateur et de la carte graphique (filtre de réduction GPU ≠ logiciel) — un liseré d'un pixel à
 * faible alpha suffit à faire basculer le critère « traits fins ». L'image est donc dessinée à sa taille NATIVE (pixels du
 * fichier, pas de filtre) puis réduite ici, par moyenne de surface exacte, pareil partout. Résultat : un tampon RGBA W×H
 * (image iw×ih posée à pad,pad), identique sur toutes les machines pour un même fichier.
 * Si le fichier est plus petit que la cible (agrandissement), on laisse drawImage : pas de liseré de réduction dans ce cas.
 */
// pixels natifs du fichier, gardés pour les largeurs suivantes du balayage (un seul getImageData par fichier)
const nativeCache = new Map<HTMLImageElement, Uint8ClampedArray>();
const nativePixels = (im: HTMLImageElement): Uint8ClampedArray | null => {
	const hit = nativeCache.get(im);
	if (hit) return hit;
	const canvas = document.createElement('canvas');
	canvas.width = im.naturalWidth;
	canvas.height = im.naturalHeight;
	const ctx = canvas.getContext('2d', { willReadFrequently: true });
	if (!ctx) return null;
	ctx.drawImage(im, 0, 0);
	const px = ctx.getImageData(0, 0, im.naturalWidth, im.naturalHeight).data;
	nativeCache.set(im, px);
	if (nativeCache.size > 2) nativeCache.delete(nativeCache.keys().next().value as HTMLImageElement);
	return px;
};

export function rasterize(im: HTMLImageElement, iw: number, ih: number, pad: number, W: number, H: number): Uint8ClampedArray | null {
	const nw = im.naturalWidth || 0;
	const nh = im.naturalHeight || 0;
	if (!nw || !nh || nw < iw || nh < ih) {
		const canvas = document.createElement('canvas');
		canvas.width = W;
		canvas.height = H;
		const ctx = canvas.getContext('2d', { willReadFrequently: true });
		if (!ctx) return null;
		ctx.drawImage(im, pad, pad, iw, ih);
		return ctx.getImageData(0, 0, W, H).data;
	}
	const src = nativePixels(im);
	if (!src) return null;
	// passe horizontale : nw → iw (prémultiplié par alpha pour garder des couleurs justes)
	const tmp = new Float32Array(iw * nh * 4);
	const sx = nw / iw;
	for (let x = 0; x < iw; x++) {
		const x0 = x * sx;
		const x1 = x0 + sx;
		const ia = Math.floor(x0);
		const ib = Math.min(nw - 1, Math.ceil(x1) - 1);
		for (let y = 0; y < nh; y++) {
			let r = 0;
			let g = 0;
			let b = 0;
			let a = 0;
			for (let i = ia; i <= ib; i++) {
				const w = Math.min(x1, i + 1) - Math.max(x0, i);
				if (w <= 0) continue;
				const o = (y * nw + i) * 4;
				const al = src[o + 3] * w;
				r += src[o] * al;
				g += src[o + 1] * al;
				b += src[o + 2] * al;
				a += al;
			}
			const t = (y * iw + x) * 4;
			tmp[t] = r;
			tmp[t + 1] = g;
			tmp[t + 2] = b;
			tmp[t + 3] = a;
		}
	}
	// passe verticale : nh → ih, puis dépôt dans le tampon de sortie
	const out = new Uint8ClampedArray(W * H * 4);
	const sy = nh / ih;
	const area = sx * sy;
	for (let y = 0; y < ih; y++) {
		const y0 = y * sy;
		const y1 = y0 + sy;
		const ja = Math.floor(y0);
		const jb = Math.min(nh - 1, Math.ceil(y1) - 1);
		for (let x = 0; x < iw; x++) {
			let r = 0;
			let g = 0;
			let b = 0;
			let a = 0;
			for (let j = ja; j <= jb; j++) {
				const w = Math.min(y1, j + 1) - Math.max(y0, j);
				if (w <= 0) continue;
				const t = (j * iw + x) * 4;
				r += tmp[t] * w;
				g += tmp[t + 1] * w;
				b += tmp[t + 2] * w;
				a += tmp[t + 3] * w;
			}
			const o = ((y + pad) * W + x + pad) * 4;
			if (a > 0) {
				out[o] = r / a;
				out[o + 1] = g / a;
				out[o + 2] = b / a;
				out[o + 3] = a / area;
			}
		}
	}
	return out;
}

// DPI effectifs d'un raster à la taille d'impression choisie
export async function effectiveDpi(src: string, widthCm: number): Promise<number> {
	if (widthCm <= 0) return Infinity;
	const im = await loadImage(src);
	return im.naturalWidth / (widthCm / 2.54);
}

export async function hasThinStrokes(src: string, widthCm: number, heightCm: number): Promise<boolean> {
	const wMm = widthCm * 10;
	const hMm = heightCm * 10;
	if (wMm <= 0 || hMm <= 0) return false;
	const key = `${widthCm}x${heightCm}|${src}`;
	const hit = resultCache.get(key);
	if (hit !== undefined) return hit;

	const pxPerMm = Math.min(TARGET_PX_PER_MM, Math.sqrt(MAX_PIXELS / (wMm * hMm)));
	const r = (MIN_STROKE_MM / 2) * pxPerMm;
	const r3 = Math.round(3 * r);
	const pad = Math.ceil(r) + 2;
	const iw = Math.max(1, Math.round(wMm * pxPerMm));
	const ih = Math.max(1, Math.round(hMm * pxPerMm));
	const W = iw + 2 * pad;
	const H = ih + 2 * pad;

	const im = await loadImage(src);
	const data = rasterize(im, iw, ih, pad, W, H);
	if (!data) return false;
	// 09.10.2026 : la poussière de détourage (îlots < 0,1 mm²) n'est pas un trait — sans ça, quelques points
	// isolés dans le fond suffisaient à déclencher « traits trop fins » (critère « loin des zones épaisses »)
	// seuil = celui du nettoyage automatique, en pixels SOURCE (la poussière a la taille du fichier, pas celle de
	// l'impression), ramené à l'échelle d'analyse ; plancher physique 0,1 mm²
	const srcArea = autoSpeckleArea(im.naturalWidth || 1, im.naturalHeight || 1) * ((iw * ih) / Math.max(1, (im.naturalWidth || 1) * (im.naturalHeight || 1)));
	despeckle(data, W, H, Math.max(dustAreaPx(pxPerMm), Math.round(srcArea)));

	const n = W * H;
	const INF = 0x3fffffff;
	const ink = new Uint8Array(n);
	const dist = new Int32Array(n);
	let inkCount = 0;
	for (let i = 0; i < n; i++) {
		if (data[i * 4 + 3] > 32) {
			ink[i] = 1;
			inkCount++;
			dist[i] = INF;
		}
	}
	if (!inkCount) {
		resultCache.set(key, false);
		return false;
	}

	// distance de chaque pixel d'encre au fond → érosion = dist > r
	chamfer(dist, W, H);
	let survivors = 0;
	const dist2 = new Int32Array(n);
	for (let i = 0; i < n; i++) {
		if (ink[i] && dist[i] > r3) {
			dist2[i] = 0;
			survivors++;
		} else {
			dist2[i] = INF;
		}
	}

	let thin = 0;
	let far = 0;
	if (!survivors) {
		thin = inkCount; // tout disparaît à l'érosion : tout est trop fin
		far = inkCount;
	} else {
		// dilatation des survivants → l'encre non recouverte est sous 0,5 mm
		chamfer(dist2, W, H);
		const far3 = 2 * r3;
		const isThin = new Uint8Array(n);
		for (let i = 0; i < n; i++) if (ink[i] && dist2[i] > r3 + DILATE_SLACK) isThin[i] = 1;
		// même règle que la correction : seuls les vrais traits comptent (pas les pointes de liseré ni la poussière collée)
		thin = keepRealThinStrokes(isThin, W, H, pxPerMm);
		for (let i = 0; i < n; i++) if (isThin[i] && dist2[i] > far3) far++;
	}

	const farAlert = far >= Math.max(MIN_FAR_PX, FAR_MM2_ALERT * pxPerMm * pxPerMm);
	const tooThin = (thin >= MIN_THIN_PX && thin / inkCount >= RATIO_ALERT) || farAlert;
	resultCache.set(key, tooThin);
	if (resultCache.size > 300) resultCache.delete(resultCache.keys().next().value as string);
	return tooThin;
}
